// The DOM chrome's styles. Vite handles CSS natively, so a stylesheet is one import rather than a
// build step — see design/UI.md on why the chrome outgrew `index.html`'s `<style>` block.
import "./chrome.css";

import { Fitout, resolveFitout } from "../shared/Fitout";
import { loadoutOf, StoredPlayerData } from "./scripts/data/Player";
import { player } from "./scripts/data/LocalStore";
import { Game } from "./scripts/Game";
import { BattleScene } from "./scripts/scenes/BattleScene";
import { MainScene } from "./scripts/scenes/MainScene";
import { Preloader } from "./scripts/scenes/Preloader";
import { FpsMeter } from "./scripts/ui/FpsMeter";
import { Notice } from "./scripts/ui/Notice";
import { Shop } from "./scripts/ui/Shop";
import { Toolbar } from "./scripts/ui/Toolbar";

/**
 * What the player is told when the arena stops answering. It says the one thing they cannot work
 * out for themselves and would otherwise wonder about all the way back to the menu: the ship is
 * still out there. See design/Battle.md on ghosts.
 */
/**
 * Where the attract boat stands off to while the shop is open, as fractions of the window.
 *
 * Down and to the right, because the panel is anchored top-left and sized to its own content. Far
 * enough to clear it on an ordinary window, not so far that the hull is half off the screen — and
 * on a window too narrow for both, the panel simply wins and the boat is seen through its wash.
 */
const SHOP_PREVIEW = { x: 0.74, y: 0.7 };

const LOST_TITLE = "Connection lost";
const LOST_BODY =
    "The arena stopped answering. Your ship is still out there — it sails on as a ghost until somebody sinks it, and its plunder goes to whoever does.";

/** The boat in the locker, resolved for the attract screen to sail. Level 0: this is a preview. */
function loadoutFitout(saved: StoredPlayerData): Fitout {
    return resolveFitout(loadoutOf(saved.locker.equipped), 0);
}

async function start(): Promise<void> {
    await Game.boot();
    await Game.changeScene(new Preloader());

    // What this device remembers: the name, the wallet, and the boat in the locker. Read once, up
    // front, so nothing downstream has to wonder whether it is first — and awaited, because the
    // next implementation of this is a request rather than a parse. See design/AccountData.md.
    const saved = await player.load();

    // After the preloader, so the bar does not sit over the loading screen — and so the
    // title font it borrows has already loaded.
    const toolbar = new Toolbar();
    // The stored name if there is one, and the guest label if there is not. It is a *display* of
    // `displayName` rather than a second source of truth for it, which is the rule design/UI.md
    // set when this was still listed as not wired up.
    if (saved.displayName.length > 0) toolbar.user = saved.displayName;
    toolbar.purse = saved.wallet.doubloons;
    // Typed once and remembered. An empty box is left alone by the store rather than overwriting a
    // real name with a placeholder, so clearing it and walking away costs nothing.
    toolbar.onRename = (name) => void player.setName(name);
    toolbar.mount();

    // Mounted once and reused, like the toolbar: a dialog created at the moment it is needed is
    // one more thing that can fail on the frame everything else already has.
    const notice = new Notice();
    notice.mount();

    // Counts every frame of every scene, so it is mounted here beside the other chrome rather
    // than by whichever scene happens to be up. See design/UI.md.
    const fps = new FpsMeter();
    fps.mount();

    // The shop. Mounted once and opened and shut, like the notice — and over the attract sea
    // rather than instead of it, which is what makes equipping a boat visible on a whole ship
    // under way. See design/Shop.md.
    const shop = new Shop(player);
    shop.mount();

    /** Drops the preview boat clear of the shop panel while it is up, and back when it is not. */
    const framePreview = () => {
        if (shop.visible) preview?.focusOn(SHOP_PREVIEW.x, SHOP_PREVIEW.y);
        else preview?.focusOn(0.5, 0.5);
    };

    toolbar.onShop = () => void shop.open().then(framePreview);
    shop.onClosed = framePreview;

    // The toolbar owns the button; scene changes are decided here, so neither the bar nor
    // the battle knows about the other.
    let switching = false;
    /**
     * What the battle being torn down earned, set by its `onEarned` as `changeScene` destroys it.
     *
     * A variable rather than a `void player.bank(...)` inside the callback, because **the order
     * matters**: the header shows the wallet the moment a player comes ashore, and a fire-and-forget
     * bank would let that read land before the write did. The number would be a run behind — right
     * again after the next reload, which is the worst kind of wrong.
     */
    let earned = 0;
    /**
     * The attract scene, while there is one. Held because the shop talks to it — it refits the
     * preview on a purchase and moves it out from behind the panel — and `main.ts` is the one
     * place that knows both exist. See design/Shop.md.
     */
    let preview: MainScene | null = null;

    /**
     * Puts the player to sea or brings them ashore. One function for both directions, because
     * there are now two ways of coming back — the Leave button, and being sunk (design/Battle.md)
     * — and they have to leave the header and the scene in exactly the same state.
     */
    const showBattle = async (joining: boolean): Promise<void> => {
        if (switching) return;
        switching = true;

        toolbar.inBattle = joining;

        // The guns only go one way now: the scene aims and fires them off the mouse
        // (design/Controls.md), and the header is where the player reads what the ship is
        // carrying and which of it is loaded. Both hand-offs are wired here for the reason the
        // Battle button is — the bar has no idea what an arena is, and the scene has no idea
        // there is a header. See design/Evolution.md.
        //
        // The boat is the one in the locker, resolved through the same clamp the arena runs on a
        // joining client: a preset that has left the catalogue since it was equipped becomes the
        // free one rather than a number nothing can resolve. See design/AccountData.md.
        const battle = joining ? new BattleScene(toolbar.user, loadoutOf((await player.load()).locker.equipped)) : null;
        if (battle) {
            battle.onBattery = (cannons) => (toolbar.battery = cannons);
            battle.onFired = (cannon) => toolbar.fired(cannon);
            // What the run has earned. It stays up after the battle ends, showing what the last
            // one was worth, until there is a wallet for it to become — see design/Shop.md.
            battle.onPurse = (doubloons) => (toolbar.purse = doubloons);
            // **Banked once, whichever way the life ended.** Sunk, Leave and a lost connection all
            // come through here, and each of them hands over the last purse this client saw —
            // which on the lost-connection route is the last patch before the socket died, and is
            // the honest number. See design/AccountData.md.
            battle.onEarned = (doubloons) => (earned = doubloons);
            // A life ended. The scene has already stopped updating itself by the time this runs,
            // so changing it out from under the ticker is safe — see `BattleScene._checkSunk`.
            battle.onSunk = () => void showBattle(false);
            // The arena went away. Unlike a sinking this is not the game working, so the player
            // is told why before anything moves: the scene is frozen behind the notice, and only
            // answering it puts them back ashore. See src/scripts/ui/Notice.ts.
            battle.onLost = () => void notice.show(LOST_TITLE, LOST_BODY).then(() => showBattle(false));
        }

        // The frame counter grows a latency line while there is an arena to have one to, and
        // loses it again ashore. A function rather than a value: the meter pulls it twice a
        // second on its own window rather than being told on every frame of a battle.
        fps.latency = battle ? () => battle.latency : null;

        // Nothing to buy at sea, and a storefront left open over a battle would be a screen
        // between the player and the thing shooting at them.
        shop.close();

        earned = 0;
        // Destroying the outgoing scene is what fires `onEarned`, so the run's takings are only
        // known once this has resolved.
        preview = joining ? null : new MainScene(loadoutFitout(await player.load()));
        await Game.changeScene(battle ?? preview!);

        framePreview();
        // The attract sea is the shop's preview, so a purchase has to reach it — a boat equipped
        // on a card becomes the boat on the water a frame later, which is the whole reason the
        // shop is an overlay rather than a screen of its own.
        shop.onChanged = preview
            ? async () => {
                  const saved = await player.load();
                  if (preview) preview.fitout = loadoutFitout(saved);
                  toolbar.purse = saved.wallet.doubloons;
              }
            : null;

        // **Banked before the header is allowed to read the wallet.** Both are awaited and in this
        // order, so a player who comes ashore sees the number the run they just finished actually
        // produced rather than the one from the run before it.
        if (!joining) {
            await player.bank(earned);
            toolbar.purse = (await player.load()).wallet.doubloons;
        }

        switching = false;
    };

    toolbar.onBattle = () => void showBattle(!toolbar.inBattle);

    // Through the same path a Leave takes, rather than building a `MainScene` here as well. It is
    // one line shorter and, more to the point, `showBattle` is where the attract scene is handed
    // to the shop — a boot that made its own would give the shop nothing to preview on.
    await showBattle(false);
}

start();
